import classNames from 'classnames'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { type IETFLanguageTag, type WeekStartsOn } from '@opallabs/concepts'
import { Size } from '@opallabs/gem'
import { Button, ButtonSize, DotLoader, Icon } from '@opallabs/lapidary'
import { visibleCalendarFilterCategories } from '../categories'
import { type CalendarResultCounts } from '../resultCounts'
import {
  type CalendarItemKind,
  type CalendarFilterCategory,
  type CalendarFilterOption,
  EMPTY_CATEGORY_FILTER,
} from '../types'
import { type UseCalendarFiltersResult } from '../useCalendarFilters'
import { buildValueTree, parentFilterMode } from '../valueTree'
import { CategoryList } from './CategoryList'
import { DrawerFooter } from './DrawerFooter'
import { LiveRangeLeaf } from './LiveRangeLeaf'
import { builtInOptionIcon } from './OptionIcon'
import { ChildOptionLeaf, LeafSearch, OptionLeaf } from './OptionLeaf'
import { calendarFilterMatches, SearchResults } from './SearchResults'
import * as styles from './styles'

export { selectors as calendarFilterSelectors } from './styles'

/**
 * Which category the drawer is inside, and which row of it. A `parentId` is the
 * tier under that row: the accounts of a channel, the labels of a set.
 */
type OpenLeaf = Readonly<{ categoryId: string; parentId?: string }>

export type CalendarFilterDrawerProps = Readonly<{
  categories: readonly CalendarFilterCategory[]
  className?: string
  /** The filter state and the edits to it, from `useCalendarFilters`. */
  filters: UseCalendarFiltersResult
  /** The option lists are still on their way, so there is nothing to offer yet. */
  isLoading?: boolean
  /** Reads the dates of the live window back in the viewer's own words. */
  locale?: IETFLanguageTag
  onClose: () => void
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
  /**
   * How much of the calendar these filters leave on screen, by kind. The
   * surface counts it, because the drawer never sees the items: it is the
   * answer to what a person just chose, and the footer says it back to them.
   */
  resultCounts: CalendarResultCounts
  /** The kinds of item on the calendar now. Decides which categories appear. */
  shownKinds: readonly CalendarItemKind[]
  /** The day the date pickers start their weeks on. */
  weekStartsOn?: WeekStartsOn
}>

/**
 * The filter drawer: a list of categories, then one category, then one row of
 * it. A search over the list reaches every value of every category at once. A
 * value takes effect as it is chosen — filtering happens in the browser,
 * so there is nothing to apply.
 *
 * The drawer knows nothing about where option data comes from. Callers build
 * the category list, which is how an adapter adds categories of its own.
 */
export const CalendarFilterDrawer: React.FC<CalendarFilterDrawerProps> = ({
  categories,
  className,
  filters,
  isLoading = false,
  locale,
  onClose,
  renderOptionIcon,
  resultCounts,
  shownKinds,
  weekStartsOn,
}) => {
  const [leaf, setLeaf] = useState<OpenLeaf | null>(null)
  const [search, setSearch] = useState('')

  // Only the surface can draw a channel's artwork, so it says what it can and
  // the built-in categories — statuses, privacy — mark themselves.
  const renderIcon = (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) =>
    renderOptionIcon?.(category, option) ?? builtInOptionIcon(category, option)

  const shownCategories = useMemo(
    () => visibleCalendarFilterCategories(categories, shownKinds),
    [categories, shownKinds]
  )
  const leafCategory =
    shownCategories.find((category) => category.id === leaf?.categoryId) ?? null
  // Searching replaces the category list rather than narrowing it: a person
  // looking for an account should not have to know which channel it is under,
  // or that it is an account at all. Null means nobody is searching.
  const matches =
    search.trim() === '' ? null : calendarFilterMatches(shownCategories, search)
  // A category can stop being shown while its leaf is open — the Show set
  // changes — and the drawer has to fall back to the list rather than slide to
  // an empty pane. An option list can change under an open tier the same way,
  // which is why the open row is looked up rather than held.
  const isLeafOpen = leafCategory !== null
  // A window of dates rather than a list of values, so the tier under this row
  // is two fields and there is nothing below it to open.
  const isLiveOpen = leafCategory?.picker === 'dateRange'
  const openParent =
    leafCategory === null || leaf?.parentId === undefined
      ? null
      : (leafCategory.options.find((option) => option.id === leaf.parentId) ??
        null)
  const isChildOpen = openParent !== null
  const leafFilter =
    leafCategory === null
      ? EMPTY_CATEGORY_FILTER
      : (filters.state.cats[leafCategory.id] ?? EMPTY_CATEGORY_FILTER)

  // The tree the open leaf reads and writes through: a chosen channel stands
  // for its accounts, a chosen label set for its labels.
  const leafTree = useMemo(
    () => buildValueTree(leafCategory?.options ?? [], leafCategory?.modeScope),
    [leafCategory]
  )

  // Whose choice Include or Exclude is here. A category that makes it once
  // carries it down to the tier under a row, which says so and cannot change
  // it; a category whose rows each answer for themselves has a control on the
  // row and the same control again on the tier the row opens.
  const isLeafByParent = leafTree.modeScope === 'parent'
  const leafMode = leafFilter.mode ?? 'include'
  const childMode =
    isLeafByParent && openParent !== null
      ? parentFilterMode(openParent.id, leafFilter, leafTree)
      : leafMode

  const closeLeaf = () => setLeaf(null)
  const closeChild = () =>
    setLeaf((leaf) => (leaf === null ? null : { categoryId: leaf.categoryId }))
  const goBack = isChildOpen ? closeChild : closeLeaf

  // Escape steps back out one tier at a time, then out of the drawer.
  const rootRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const root = rootRef.current
    if (root === null) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      if (isChildOpen) closeChild()
      else if (isLeafOpen) closeLeaf()
      else onClose()
    }

    root.addEventListener('keydown', onKeyDown)
    return () => root.removeEventListener('keydown', onKeyDown)
  }, [isChildOpen, isLeafOpen, onClose])

  // Each tier replaces the rows the keyboard was on rather than opening beside
  // them, so focus has to be handed over both ways or the drawer loses its
  // place — and with it Escape, which only answers from inside the drawer. On
  // the way in that is the way back out; on the way out, the row it came from.
  const backRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (isChildOpen) backRef.current?.focus()
  }, [isChildOpen])

  const categoryRows = useRef(new Map<string, HTMLButtonElement | null>())
  const openCategoryId = useRef<string | null>(null)
  const leafCategoryId = leafCategory?.id ?? null
  useEffect(() => {
    if (leafCategoryId === null && openCategoryId.current !== null)
      categoryRows.current.get(openCategoryId.current)?.focus()
    openCategoryId.current = leafCategoryId
  }, [leafCategoryId])

  const title = isChildOpen
    ? openParent.label
    : (leafCategory?.label ?? 'Filters')
  const backLabel =
    leafCategory !== null && isChildOpen
      ? `Back to ${leafCategory.label}`
      : 'Back to all filters'

  return (
    <section
      ref={rootRef}
      aria-label="Filters"
      className={classNames(styles.selectors.root, className)}
      css={styles.root}
    >
      <header className={styles.selectors.header}>
        {!isLeafOpen ? null : (
          <Button
            ref={backRef}
            ariaLabel={backLabel}
            icon={
              <Icon
                ariaHidden={true}
                className={styles.selectors.lineArrow}
                glyph="gem-chevron-w"
                size={Size.Icon}
              />
            }
            name={backLabel}
            onClick={goBack}
            size={ButtonSize.Small}
            variant="ghost"
          />
        )}
        <div className={styles.selectors.headerTitle}>
          <h2>{title}</h2>
        </div>
        <Button
          ariaLabel="Close filters"
          icon={<Icon ariaHidden={true} glyph="gem-close" size={Size.Icon} />}
          name="Close filters"
          onClick={onClose}
          size={ButtonSize.Small}
          variant="ghost"
        />
      </header>

      {isLoading ? (
        <div className={styles.selectors.loading}>
          <DotLoader />
        </div>
      ) : (
        <div
          className={styles.selectors.panes}
          style={
            {
              [styles.cssVariables.paneShift]:
                styles.paneShifts[isChildOpen ? 2 : isLeafOpen ? 1 : 0],
            } as CSSProperties
          }
        >
          {/*
            Every pane stays mounted so the track can slide, which leaves the
            ones off screen tabbable. `inert` takes a pane out of the tab order
            and out of the accessibility tree, and the stylesheet hides it on
            the same attribute for browsers that do not know it yet.
          */}
          <div inert={isLeafOpen ? '' : undefined}>
            <div className={styles.selectors.categorySearch}>
              <LeafSearch
                context="Filters"
                htmlId="calendar-filter-search-all"
                label="Search filters"
                onSearch={setSearch}
              />
            </div>

            {matches === null ? (
              <CategoryList
                categories={shownCategories}
                chosen={filters.state}
                locale={locale}
                onOpen={(categoryId) => setLeaf({ categoryId })}
                onRemove={filters.removeValue}
                onSetLiveRange={filters.setLiveRange}
                renderOptionIcon={renderIcon}
                rowRef={(categoryId, row) => {
                  categoryRows.current.set(categoryId, row)
                }}
              />
            ) : (
              <SearchResults
                chosen={filters.state}
                matches={matches}
                onToggleValue={filters.toggleValue}
                renderOptionIcon={renderIcon}
              />
            )}
          </div>

          <div inert={isLeafOpen && !isChildOpen ? undefined : ''}>
            {leafCategory === null ? null : isLiveOpen ? (
              <LiveRangeLeaf
                onChange={filters.setLiveRange}
                range={filters.state.live ?? {}}
                weekStartsOn={weekStartsOn}
              />
            ) : (
              <OptionLeaf
                key={leafCategory.id}
                category={leafCategory}
                exclude={leafFilter.exclude}
                include={leafFilter.include}
                mode={leafMode}
                onModeChange={(mode) => filters.setMode(leafCategory.id, mode)}
                onOpenParent={(parentId) =>
                  setLeaf({ categoryId: leafCategory.id, parentId })
                }
                onSetParentMode={(parentId, mode) =>
                  filters.setParentMode(
                    leafCategory.id,
                    parentId,
                    mode,
                    leafTree
                  )
                }
                onToggleAll={(optionIds) =>
                  filters.toggleAllValues(leafCategory.id, optionIds, leafTree)
                }
                onToggleValue={(valueId) =>
                  filters.toggleValue(leafCategory.id, valueId, leafTree)
                }
                openParentId={openParent?.id ?? null}
                parentModes={leafFilter.parentModes}
                renderOptionIcon={renderIcon}
              />
            )}
          </div>

          <div inert={isChildOpen ? undefined : ''}>
            {leafCategory === null || openParent === null ? null : (
              <ChildOptionLeaf
                key={`${leafCategory.id}:${openParent.id}`}
                category={leafCategory}
                exclude={leafFilter.exclude}
                include={leafFilter.include}
                mode={childMode}
                onModeChange={
                  !isLeafByParent
                    ? undefined
                    : (mode) =>
                        filters.setParentMode(
                          leafCategory.id,
                          openParent.id,
                          mode,
                          leafTree
                        )
                }
                onToggleAll={(optionIds) =>
                  filters.toggleAllValues(leafCategory.id, optionIds, leafTree)
                }
                onToggleValue={(valueId) =>
                  filters.toggleValue(leafCategory.id, valueId, leafTree)
                }
                parent={openParent}
                renderOptionIcon={renderIcon}
              />
            )}
          </div>
        </div>
      )}

      <DrawerFooter
        counts={resultCounts}
        isEmpty={filters.count === 0}
        onClearAll={filters.clearAll}
        onDone={onClose}
        shownKinds={shownKinds}
      />
    </section>
  )
}
CalendarFilterDrawer.displayName = 'Calendar: CalendarFilterDrawer'
